import React, { useState } from 'react';

/**
 * Interactive Setup Guide Component
 * Provides step-by-step integration guides for each API service
 */
const SetupGuide = ({ serviceId }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [base64Result, setBase64Result] = useState('');
    const [fileName, setFileName] = useState('');
    const [isConverting, setIsConverting] = useState(false);

    const copyCode = (code, index) => {
        navigator.clipboard.writeText(code);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            alert('Please upload a PDF file only.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert('File size must be less than 10MB.');
            return;
        }

        setIsConverting(true);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = () => {
            const base64String = reader.result.split(',')[1]; // Remove data:... prefix
            setBase64Result(base64String);
            setIsConverting(false);
        };
        reader.onerror = () => {
            alert('Error reading file.');
            setIsConverting(false);
        };
        reader.readAsDataURL(file);
    };

    const copyBase64 = () => {
        navigator.clipboard.writeText(base64Result);
        setCopiedIndex('base64');
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    // Get guide content based on service
    const guides = {
        doqsolv: {
            title: "DoqSolv API Integration",
            description: "Follow these steps to integrate DoqSolv document generation into your application.",
            steps: [
                {
                    title: "Get Your API Key",
                    description: "Your API key is available in the API section above. Copy it and store it securely.",
                    tip: "Never expose your API key in client-side code. Always call from your backend server."
                },
                {
                    title: "Set Up Authentication",
                    description: "Include your API key in the X-API-Key header for all requests.",
                    code: `// Add this header to all API requests
const headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
};`,
                    language: "javascript"
                },
                {
                    title: "List Global Templates",
                    description: "Retrieve a list of available global templates to use for document generation.",
                    code: `// GET /api/external/v1/doqsolv/templates?scope=global
const response = await fetch(
    'https://oms.matrica.in/api/external/v1/doqsolv/templates?scope=global',
    { headers: { 'X-API-Key': 'YOUR_API_KEY' } }
);

const templates = await response.json();
console.log(templates);`,
                    language: "javascript",
                    tip: "Find the template_id from the response to use in document generation."
                },
                {
                    title: "Generate a Document",
                    description: "Generate a PDF document by providing variable values for a given template.",
                    code: `// POST /api/external/v1/doqsolv/generate
const response = await fetch(
    'https://oms.matrica.in/api/external/v1/doqsolv/generate',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            template_id: 'TEMPLATE_ID',
            variables: {
                first_party_name: "John Doe",
                second_party_name: "Jane Smith",
                consideration_amount: "500000"
            },
            order_id: 'OPTIONAL_ORDER_ID' // E.g. to link with stamp duty
        })
    }
);

const data = await response.json();
// Response structure:
// {
//   success: true,
//   document_id: "doc_uuid_123",
//   template_name: "Agreement",
//   pdf_file_id: "gridfs_id",
//   message: "Document generated successfully"
// }`,
                    language: "javascript",
                    tip: "Pass variables matching the placeholders in our template definition."
                },
                {
                    title: "Download Document",
                    description: "Download the generated document using the document_id retrieved in the previous step.",
                    code: `// GET /api/external/v1/doqsolv/documents/{document_id}/download?format=pdf
const documentId = 'doc_uuid_123';

const response = await fetch(
    \`https://oms.matrica.in/api/external/v1/doqsolv/documents/\${documentId}/download?format=pdf\`,
    {
        headers: { 
            'X-API-Key': 'YOUR_API_KEY'
        }
    }
);

// This endpoint returns binary PDF data
const blob = await response.blob();
const url = URL.createObjectURL(blob);
const a = document.createElement('a');
a.href = url;
a.download = 'generated_document.pdf';
a.click();`,
                    language: "javascript",
                    tip: "This endpoint provides binary PDF streams suitable for immediate download."
                }
            ]
        },
        docu_stamp: {
            title: "Docu Stamp API Integration",
            description: "Follow these steps to integrate document retrieval into your application.",
            steps: [
                {
                    title: "Get Your API Key",
                    description: "Your API key is available in the API section above. Copy it and store it securely.",
                    tip: "Never expose your API key in client-side code. Always call from your backend server."
                },
                {
                    title: "Set Up Authentication",
                    description: "Include your API key in the X-API-Key header for all requests.",
                    code: `// Add this header to all API requests
const headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
};`,
                    language: "javascript"
                },
                {
                    title: "List Available Documents",
                    description: "Get a list of available documents for an order.",
                    code: `// GET /api/external/v1/documents/available
const response = await fetch(
    'https://oms.matrica.in/api/external/v1/documents/available?order_id=ORDER_123',
    { headers: { 'X-API-Key': 'YOUR_API_KEY' } }
);

const documents = await response.json();
console.log(documents);`,
                    language: "javascript"
                },
                {
                    title: "Consume Documents",
                    description: "Consume the next available document and get Base64-encoded file content with metadata.",
                    code: `// POST /api/external/v1/documents/consume
const response = await fetch(
    'https://oms.matrica.in/api/external/v1/documents/consume',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            request_id: 'req_unique_123', // Must be globally unique
            order_id: 'ORDER_123'
        })
    }
);

const data = await response.json();
// Response structure:
// {
//   status: "success",
//   document_id: "doc_uuid",
//   mime_type: "application/pdf",
//   file_size: 12345,
//   file_base64: "JVBERi0xLjQK...",
//   metadata: {
//     request_id: "req_unique_123",
//     order_id: "ORDER_123",
//     certificate_id: "cert_456",
//     page_number: 1,
//     consumed_at: "2023-12-01T10:30:00",
//     created_at: "2023-12-01T09:00:00"
//   }
// }

// Decode Base64 to get file content
const binaryString = atob(data.file_base64);
const bytes = new Uint8Array(binaryString.length);
for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
}
const blob = new Blob([bytes], { type: data.mime_type });

// Download or use the file
const url = URL.createObjectURL(blob);
const a = document.createElement('a');
a.href = url;
a.download = \`document_\${data.document_id}.pdf\`;
a.click();`,
                    language: "javascript",
                    tip: "The consume endpoint returns JSON with Base64 data, not binary. Decode the Base64 string to get the actual file."
                },
                {
                    title: "Download Documents",
                    description: "Download a document as binary PDF. Requires request_id query parameter for consumption tracking.",
                    code: `// GET /api/external/v1/documents/{id}/download
const documentId = 'DOC_ID';
const requestId = 'req_unique_123'; // Required for consumption tracking

const response = await fetch(
    \`https://oms.matrica.in/api/external/v1/documents/\${documentId}/download?request_id=\${requestId}\`,
    {
        headers: { 
            'X-API-Key': 'YOUR_API_KEY'
        }
    }
);

// This endpoint returns binary PDF (not JSON)
const blob = await response.blob();
const url = URL.createObjectURL(blob);
const a = document.createElement('a');
a.href = url;
a.download = 'document.pdf';
a.click();

// Get metadata from response headers
const docId = response.headers.get('X-Document-ID');
const orderId = response.headers.get('X-Order-ID');
console.log(\`Downloaded document \${docId} for order \${orderId}\`);`,
                    language: "javascript",
                    tip: "This endpoint returns binary PDF data (different from consume which returns JSON). Use this for direct file streaming."
                },
                {
                    title: "Get Document by Request ID",
                    description: "Retrieve a previously consumed document using its request ID. Returns JSON with Base64 content.",
                    code: `// GET /api/external/v1/documents/by-request/{request_id}
const response = await fetch(
    'https://oms.matrica.in/api/external/v1/documents/by-request/req_unique_123',
    {
        headers: { 
            'X-API-Key': 'YOUR_API_KEY'
        }
    }
);

const data = await response.json();
// Same structure as consume endpoint response
// Contains file_base64, metadata, etc.

// Decode and use the file
const binaryString = atob(data.file_base64);
const bytes = new Uint8Array(binaryString.length);
for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
}
const blob = new Blob([bytes], { type: data.mime_type });`,
                    language: "javascript"
                }
            ]
        },
        esign: {
            title: "eSign API Integration",
            description: "Send documents for electronic signature using Aadhaar-based eKYC.",
            steps: [
                {
                    title: "Get Your API Key",
                    description: "Your API key is available in the API section above. Copy it and store it securely.",
                    tip: "The same API key works for all enabled services."
                },
                {
                    title: "Prepare Your Document",
                    description: "Convert your PDF document to Base64 format.",
                    code: `// Node.js: Convert PDF to Base64
const fs = require('fs');
const pdfBuffer = fs.readFileSync('document.pdf');
const base64Data = pdfBuffer.toString('base64');

// Python
import base64
with open('document.pdf', 'rb') as f:
    base64_data = base64.b64encode(f.read()).decode('utf-8')`,
                    language: "javascript",
                    tip: "Maximum file size: 10MB before encoding"
                },
                {
                    title: "Send Document for Signature",
                    description: "Send a document to a single signer.",
                    code: `// POST /api/external/esign/send
const response = await fetch(
    'https://oms.matrica.in/api/external/esign/send',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            signer_name: "John Doe",
            signer_email: "john@example.com",
            signer_mobile: "9876543210",
            document_name: "Agreement.pdf",
            document_data: "JVBERi0xLjQK...", // Base64 data
            reason: "Account Opening",
            days_to_expire: 7,
            esign_type: "EKYC",
            signature_coordinates: [{
                page_number: 1,
                x_coordinate: 130,
                y_coordinate: 100,
                width: 103,
                height: 50
            }],
            redirect_url: "https://your-app.com/signed",
            cancel_redirect_url: "https://your-app.com/cancelled",
            remove_preview_pdf: false,
            need_name_match: true,
            percentage_name_match: 80,
            need_aadhaar_match: true,
            aadhaar_number: "1234",
            need_gender_match: true,
            gender: "M",
            debit: false
        })
    }
);

const result = await response.json();
// {
//   success: true,
//   envelope_id: "abc-123-xyz",
//   esign_url: "https://esign.meon.co.in/..."
// }`,
                    language: "javascript"
                },
                {
                    title: "Check Signature Status",
                    description: "Poll for the status of your envelope.",
                    code: `// GET /api/external/esign/status/{envelope_id}
const response = await fetch(
    'https://oms.matrica.in/api/external/esign/status/abc-123-xyz',
    { headers: { 'X-API-Key': 'YOUR_API_KEY' } }
);

const status = await response.json();
// status: "sent" | "delivered" | "signed" | "declined"`,
                    language: "javascript"
                },
                {
                    title: "Download Signed Document",
                    description: "Once signed, download the completed document.",
                    code: `// GET /api/external/esign/download/{envelope_id}
const response = await fetch(
    'https://oms.matrica.in/api/external/esign/download/abc-123-xyz',
    { headers: { 'X-API-Key': 'YOUR_API_KEY' } }
);

// Response is the PDF binary
const blob = await response.blob();
const url = URL.createObjectURL(blob);
window.open(url);`,
                    language: "javascript"
                },
                {
                    title: "Handle Multiple Signers (Optional)",
                    description: "Send to multiple signers in sequence.",
                    code: `// POST /api/external/esign/multi-send
const response = await fetch(
    'https://oms.matrica.in/api/external/esign/multi-send',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            document_name: "Partnership.pdf",
            document_data: "JVBERi0xLjQK...",
            signers: [
                {
                    name: "Partner A",
                    email: "a@example.com",
                    mobile: "9876543210",
                    sequence: 1, // Signs first
                    signature_coordinates: [{page_number: 1, x_coordinate: 100}]
                },
                {
                    name: "Partner B",
                    email: "b@example.com",
                    mobile: "9123456789",
                    sequence: 2, // Signs after Partner A
                    signature_coordinates: [{page_number: 1, x_coordinate: 300}]
                }
            ]
        })
    }
);`,
                    language: "javascript"
                }
            ]
        },
        digisign: {
            title: "Digital Signature Integration",
            description: "Integrate DigiSign for secure digital signatures via email or direct URL.",
            steps: [
                {
                    title: "Get Your API Key",
                    description: "Your API key is available in the API section above. Copy it and store it securely.",
                    tip: "The same API key works for all enabled services."
                },
                {
                    title: "Prepare Your Document",
                    description: "Convert your PDF document to Base64 format.",
                    code: `// Node.js: Convert PDF to Base64
const fs = require('fs');
const pdfBuffer = fs.readFileSync('document.pdf');
const base64Data = pdfBuffer.toString('base64');

// Python
import base64
with open('document.pdf', 'rb') as f:
    base64_data = base64.b64encode(f.read()).decode('utf-8')`,
                    language: "javascript",
                    tip: "Maximum file size: 10MB before encoding"
                },
                {
                    title: "Initiate Signature (Email Mode)",
                    description: "Send a document to be signed via email.",
                    code: `// POST /api/external/digisign/initiate
const response = await fetch(
    'https://oms.matrica.in/api/external/digisign/initiate',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            signature_mode: "email",
            document_name: "Agreement.pdf",
            document_data: "JVBERi0xLjQK...", // Base64 PDF
            recipients: [
                {
                    name: "John Doe",
                    email: "john@example.com",
                    signature_coordinate: {
                        page_number: 1,
                        x_coordinate: 100,
                        y_coordinate: 200,
                        width: 100,
                        height: 50
                    }
                }
            ]
        })
    }
);

const result = await response.json();
// { success: true, document_id: "...", message: "..." }`,
                    language: "javascript"
                },
                {
                    title: "Initiate Signature (URL Mode)",
                    description: "Get a signing URL to embed or redirect immediately.",
                    code: `// POST /api/external/digisign/initiate
const response = await fetch(
    'https://oms.matrica.in/api/external/digisign/initiate',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            signature_mode: "signing_url", // Use 'signing_url' mode
            document_name: "Agreement.pdf",
            document_data: "JVBERi0xLjQK...", 
            recipients: [
                {
                     name: "John Doe",
                     email: "john@example.com", // Email required for ID
                     signature_coordinate: { page_number: 1, x_coordinate: 100 }
                }
            ],
            redirect_url: "https://your-app.com/callback"
        })
    }
);

const result = await response.json();
// { 
//   success: true, 
//   signing_urls: [{ signing_url: "https://..." }] 
// }
// You can now redirect the user to signing_url`,
                    language: "javascript"
                }
            ]
        },
        bulk_order_management: {
            title: "Bulk Order Management API Integration",
            description: "Create and manage orders in bulk with high-performance async APIs.",
            steps: [
                {
                    title: "Get Your API Key",
                    description: "Your API key is available in the API section above. Copy it and store it securely.",
                    tip: "The same API key works for all enabled services. Never expose it in client-side code."
                },
                {
                    title: "Understand Field Requirements",
                    description: "Review the data types and validation rules for order fields.",
                    code: `// ═══════════════════════════════════════════════════════════════
// ORDER FIELD SPECIFICATIONS & VALIDATION RULES
// ═══════════════════════════════════════════════════════════════

// ┌─────────────────────────────────────────────────────────────┐
// │ REQUIRED FIELDS                                              │
// └─────────────────────────────────────────────────────────────┘

state: string
  // Must be one of 36 Indian states/UTs:
  // "Maharashtra", "Gujarat", "Delhi", "Karnataka", "Tamil Nadu",
  // "Andhra Pradesh", "Telangana", "West Bengal", "Rajasthan", etc.
  // ❌ Invalid: "MH", "mumbai", "Maharashtra India"

article: string (1-200 characters)
  // Purpose/type of E-Stamp certificate
  // Example: "Sale Agreement", "Lease Deed", "Power of Attorney"

quantity: integer (1 or more)
  // Number of stamp papers required
  // ✅ Valid: 1, 100, 50000
  // ❌ Invalid: 0, -5

denomination: integer (1 or more)
  // Stamp paper value in Rupees (must be whole number)
  // ✅ Valid: 10, 100, 5000
  // ❌ Invalid: 10.50, 0

first_party: object (REQUIRED)
  {
    name: string (1-100 chars, alphanumeric allowed)
      // ✅ Valid: "John Doe", "Company 123", "Sector-45 Association"
      // ❌ Invalid: Empty string
      
    mobile: string (Indian format, 10 digits starting with 6-9)
      // Formats accepted: "9876543210", "+919876543210"
      // ❌ Invalid: "1234567890", "987654321", "98765432100"
      
    address: string (optional, max 500 chars)
  }

// ┌─────────────────────────────────────────────────────────────┐
// │ OPTIONAL FIELDS                                              │
// └─────────────────────────────────────────────────────────────┘

second_party: object (optional, same structure as first_party)

consideration_amount: float (>= 0)
  // Transaction value in Rupees
  // Example: 5000000.00

purchased_paid_by: enum
  // "first_party" (default) or "second_party"

company_name: string (max 200 chars)

delivery_mode: enum
  // "digital" (default), "physical", or "both"
  // If "physical" or "both" → delivery_address is REQUIRED

delivery_address: string (max 500 chars)
  // Required when delivery_mode is "physical" or "both"

external_ref_id: string (max 100 chars)
  // Your reference ID for tracking

notes: string (max 1000 chars)`,
                    language: "javascript",
                    tip: "Mobile numbers must start with 6, 7, 8, or 9 and be exactly 10 digits (or +91 prefix with 10 digits)"
                },
                {
                    title: "Create Bulk Orders",
                    description: "Submit up to 500 orders in a single API call.",
                    code: `// POST /api/external/v2/orders/bulk
const response = await fetch(
    'https://oms.matrica.in/api/external/v2/orders/bulk',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            batch_reference: "BATCH-2024-001", // Optional: your reference
            orders: [
                {
                    state: "Maharashtra",
                    article: "Sale Agreement",
                    quantity: 50,
                    denomination: 100,
                    first_party: {
                        name: "Rahul Sharma",
                        mobile: "9876543210",
                        address: "123 Main St, Mumbai"
                    },
                    second_party: {
                        name: "Priya Industries Pvt Ltd",
                        mobile: "9123456789",
                        address: "456 Commercial Complex, Pune"
                    },
                    consideration_amount: 5000000,
                    delivery_mode: "digital",
                    external_ref_id: "YOUR-REF-001",
                    notes: "Urgent processing required"
                },
                {
                    state: "Gujarat",
                    article: "Lease Deed",
                    quantity: 25,
                    denomination: 200,
                    first_party: {
                        name: "ABC Corporation",
                        mobile: "7890123456"
                    },
                    delivery_mode: "physical",
                    delivery_address: "789 Industrial Area, Ahmedabad, Gujarat 380001",
                    external_ref_id: "YOUR-REF-002"
                }
                // ... up to 500 orders per request
            ]
        })
    }
);

const result = await response.json();
// Response:
// {
//   success: true,
//   batch_id: "BATCH-ABCD1234EFGH",
//   total_submitted: 2,
//   total_created: 2,
//   total_failed: 0,
//   created_orders: [
//     { index: 0, order_id: "ORD-20240124...", external_ref_id: "YOUR-REF-001", status: "created" },
//     { index: 1, order_id: "ORD-20240124...", external_ref_id: "YOUR-REF-002", status: "created" }
//   ],
//   failed_orders: [],
//   message: "Created 2 orders, 0 failed"
// }`,
                    language: "javascript",
                    tip: "The batch_id is auto-generated and can be used to track orders from the same batch. Store order_ids from the response for status checks."
                },
                {
                    title: "Get Sample Preview",
                    description: "Preview the sample file associated with an order.",
                    code: `// GET /api/external/v2/orders/{order_id}/sample?format=base64
const orderId = "ORD-20240124-ABC123";
const response = await fetch(
    \`https://oms.matrica.in/api/external/v2/orders/\${orderId}/sample?format=base64\`,
    {
        headers: { 'X-API-Key': 'YOUR_API_KEY' }
    }
);

const result = await response.json();
// {
//   success: true,
//   has_sample: true,
//   sample_base64: "JVBERi0xLjQK...", // Use this to display preview
//   mime_type: "application/pdf"
// }
//
// OPTION 2: Binary Download
// GET /api/external/v2/orders/{order_id}/sample?format=binary
// Returns raw file stream (PDF/Image) directly.`,
                    language: "javascript"
                },
                {
                    title: "Approve/Reject Sample",
                    description: "Make a decision on the submitted sample.",
                    code: `// POST /api/external/v2/orders/{order_id}/sample/decision
const response = await fetch(
    \`https://oms.matrica.in/api/external/v2/orders/\${orderId}/sample/decision\`,
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            approved: true // or false to reject
        })
    }
);

const result = await response.json();
// {
//   success: true,
//   new_status: "sample_approved",
//   message: "Sample approved successfully"
// }`,
                    language: "javascript"
                },
                {
                    title: "List Orders (with Filters)",
                    description: "List all your orders with filters and pagination (20 per page by default).",
                    code: `// GET /api/external/v2/orders
const response = await fetch(
    'https://oms.matrica.in/api/external/v2/orders?' + new URLSearchParams({
        status: 'placed',           // Optional: filter by status
        state: 'Maharashtra',       // Optional: filter by state
        start_date: '2024-01-01',   // Optional: from date
        end_date: '2024-01-31',     // Optional: to date
        batch_reference: 'BATCH-001', // Optional: filter by batch
        limit: '20',                // Results per page (max 200)
        offset: '0'                 // Pagination offset
    }),
    {
        headers: { 'X-API-Key': 'YOUR_API_KEY' }
    }
);

const result = await response.json();
// {
//   success: true,
//   pagination: {
//     total: 150,
//     limit: 20,
//     offset: 0,
//     current_page: 1,
//     total_pages: 8,
//     has_next: true,
//     has_prev: false
//   },
//   filters_applied: { status: "placed", state: "Maharashtra" },
//   orders: [
//     { order_id: "ORD-...", status: "placed", state: "Maharashtra", ... },
//     ...
//   ]
// }`,
                    language: "javascript",
                    tip: "Use pagination to navigate large datasets. The response includes has_next/has_prev for easy navigation."
                },
                {
                    title: "Check Order Status (Bulk)",
                    description: "Check status of multiple orders at once.",
                    code: `// POST /api/external/v2/orders/status/bulk
const response = await fetch(
    'https://oms.matrica.in/api/external/v2/orders/status/bulk',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            order_ids: [
                "ORD-20240124-RS-50-100-ABC123",
                "ORD-20240124-AC-25-200-DEF456"
            ]
        })
    }
);

const result = await response.json();
// {
//   total_requested: 2,
//   total_found: 2,
//   orders: [
//     {
//       order_id: "ORD-20240124-RS-50-100-ABC123",
//       status: "placed",         // placed, assigned, completed, etc.
//       external_ref_id: "YOUR-REF-001",
//       created_at: "2024-01-24T10:30:00",
//       updated_at: "2024-01-24T10:30:00"
//     },
//     ...
//   ]
// }`,
                    language: "javascript"
                },
                {
                    title: "Get Single Order Details",
                    description: "Retrieve detailed information for a specific order.",
                    code: `// GET /api/external/v2/orders/{order_id}
const orderId = "ORD-20240124-RS-50-100-ABC123";
const response = await fetch(
    \`https://oms.matrica.in/api/external/v2/orders/\${orderId}\`,
    {
        headers: { 'X-API-Key': 'YOUR_API_KEY' }
    }
);

const order = await response.json();
// {
//   order_id: "ORD-20240124-RS-50-100-ABC123",
//   status: "completed",
//   external_ref_id: "YOUR-REF-001",
//   state: "Maharashtra",
//   article: "Sale Agreement",
//   quantity: 50,
//   denomination: 100,
//   total_stamp_duty: 5000,    // quantity × denomination
//   paid_stamp_duty: 5000,
//   remaining_stamp_duty: 0,
//   first_party: { name: "...", mobile: "...", address: "..." },
//   second_party: { name: "...", mobile: "...", address: "..." },
//   delivery_mode: "digital",
//   created_at: "2024-01-24T10:30:00",
//   updated_at: "2024-01-24T15:45:00"
// }`,
                    language: "javascript"
                },
                {
                    title: "Cancel an Order",
                    description: "Cancel orders that haven't been processed yet.",
                    code: `// POST /api/external/v2/orders/{order_id}/cancel?cancellation_reason=...
const orderId = "ORD-20240124-RS-50-100-ABC123";
const reason = encodeURIComponent("Customer requested cancellation");

const response = await fetch(
    \`https://oms.matrica.in/api/external/v2/orders/\${orderId}/cancel?cancellation_reason=\${reason}\`,
    {
        method: 'POST',
        headers: { 'X-API-Key': 'YOUR_API_KEY' }
    }
);

const result = await response.json();
// {
//   success: true,
//   order_id: "ORD-20240124-RS-50-100-ABC123",
//   status: "cancelled",
//   message: "Order cancelled successfully"
// }

// ⚠️ CANCELLATION RULES:
// Only orders in these statuses can be cancelled:
// - placed
// - assigned
// - sample_submitted
// - sample_rejected
//
// Orders in these statuses CANNOT be cancelled:
// - sample_approved, executing, completed, delivered`,
                    language: "javascript",
                    tip: "Cancellation reason must be 5-500 characters. Orders already in processing cannot be cancelled."
                }
            ]
        },
        live_stamping: {
            title: "Live Stamping API Integration",
            description: "Real-time single document e-stamping integration guide.",
            steps: [
                {
                    title: "Get Available Services",
                    description: "Retrieve a list of enabled Live Stamping services to get Service IDs.",
                    code: `// GET /api/external/v1/live-stamping/services
const response = await fetch(
    'https://oms.matrica.in/api/external/v1/live-stamping/services',
    { headers: { 'X-API-Key': 'YOUR_API_KEY' } }
);

const { services } = await response.json();
// Use service.id from this list to place orders
console.log(services);`,
                    language: "javascript"
                },
                {
                    title: "Place an Order",
                    description: "Place a new stamping order. The amount will be deducted from your wallet immediately.",
                    code: `// POST /api/external/v1/live-stamping/request
const response = await fetch(
    'https://oms.matrica.in/api/external/v1/live-stamping/request',
    {
        method: 'POST',
        headers: { 
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            service_id: "SERVICE_ID_HERE",
            first_party: {
                name: "John Doe",
                mobile: "9876543210"
            },
            second_party: {
                name: "Jane Smith",
                mobile: "9123456789"
            },
            article: "Affidavit",
            denomination: 100,
            quantity: 1,
            state: "Maharashtra",
            purchased_paid_by: "first_party",
            delivery_mode: "digital",
            external_ref_id: "MY-REF-001"
        })
    }
);

const result = await response.json();
// {
//   success: true,
//   order_id: "LS-20240129-ABC123",
//   status: "placed",
//   amount: 110.0
// }`,
                    language: "javascript",
                    tip: "Ensure your wallet has sufficient funds before placing an order."
                },
                {
                    title: "Check Order Status",
                    description: "Poll the order status to see when it is completed.",
                    code: `// GET /api/external/v1/live-stamping/orders/{order_id}
const orderId = "LS-20240129-ABC123";
const response = await fetch(
    \`https://oms.matrica.in/api/external/v1/live-stamping/orders/\${orderId}\`,
    { headers: { 'X-API-Key': 'YOUR_API_KEY' } }
);

const result = await response.json();
console.log(result.order.status);`,
                    language: "javascript"
                }
            ]
        }
    };

    const guide = guides[serviceId] || guides.docu_stamp;
    const currentStep = guide.steps[activeStep];

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6" >
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        📚 {guide.title}
                    </h3>
                    <p className="text-sm text-gray-500">{guide.description}</p>
                </div>
            </div>

            {/* Step Progress */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2" >
                {
                    guide.steps.map((step, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveStep(index)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeStep === index
                                ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${activeStep === index ? 'bg-indigo-600 text-white' : 'bg-gray-400 text-white'
                                }`}>
                                {index + 1}
                            </span>
                            <span className="hidden sm:inline">{step.title}</span>
                        </button>
                    ))
                }
            </div >

            {/* Current Step Content */}
            < div className="border border-gray-200 rounded-lg p-4 bg-gray-50" >
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                    Step {activeStep + 1}: {currentStep.title}
                </h4>
                <p className="text-gray-600 mb-4">{currentStep.description}</p>

                {
                    currentStep.tip && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                            <span className="text-blue-500">💡</span>
                            <p className="text-sm text-blue-700">{currentStep.tip}</p>
                        </div>
                    )
                }

                {
                    currentStep.code && (
                        <div className="relative">
                            <div className="absolute top-2 right-2 z-10">
                                <button
                                    onClick={() => copyCode(currentStep.code, activeStep)}
                                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${copiedIndex === activeStep
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                        }`}
                                >
                                    {copiedIndex === activeStep ? '✓ Copied!' : 'Copy'}
                                </button>
                            </div>
                            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                                <code>{currentStep.code}</code>
                            </pre>
                        </div>
                    )
                }

                {/* Interactive Base64 Converter - Show on "Prepare Your Document" step */}
                {
                    currentStep.title === "Prepare Your Document" && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                            <h5 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                ⚡ Quick Convert (Interactive)
                            </h5>
                            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                    <label className="flex-shrink-0 cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                                        📄 Upload PDF
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                        />
                                    </label>
                                    {fileName && (
                                        <span className="text-sm text-gray-600">
                                            {isConverting ? '⏳ Converting...' : `✅ ${fileName}`}
                                        </span>
                                    )}
                                </div>

                                {base64Result && (
                                    <div className="mt-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-gray-700">Base64 Output:</span>
                                            <button
                                                onClick={copyBase64}
                                                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${copiedIndex === 'base64'
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                    }`}
                                            >
                                                {copiedIndex === 'base64' ? '✓ Copied!' : '📋 Copy Base64'}
                                            </button>
                                        </div>
                                        <div className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono overflow-x-auto max-h-32 overflow-y-auto">
                                            {base64Result.substring(0, 500)}...
                                            <span className="text-gray-500"> ({base64Result.length.toLocaleString()} chars)</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            💡 Use this Base64 string as the <code className="bg-gray-200 px-1 rounded">document_data</code> value in your API request.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }
            </div >

            {/* Navigation */}
            < div className="flex justify-between mt-4" >
                <button
                    onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                    disabled={activeStep === 0}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    ← Previous
                </button>
                <button
                    onClick={() => setActiveStep(Math.min(guide.steps.length - 1, activeStep + 1))}
                    disabled={activeStep === guide.steps.length - 1}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next →
                </button>
            </div >
        </div >
    );
};

export default SetupGuide;
